/**
 * usePushNotifications
 *
 * Manages the browser-level Web Push subscription for this device.
 *
 * Supports two subscription modes:
 *  1. Team-based  — subscribe(teamId) follows a team; the subscription row in
 *                   push_subscriptions has team_ids=[teamId, ...] and is used
 *                   by the fanOut action to send game result pushes.
 *  2. Profile-based — subscribe("__profile__", profileId) links this device to
 *                   a user's profile_id with team_ids=[]. The sendToProfile
 *                   action in the Edge Function uses this to deliver per-user
 *                   push notifications (game scores, photo albums, etc.).
 *
 * Both modes can coexist: a subscription row can have both a profile_id and
 * team_ids, enabling both fan-out and per-user targeting.
 *
 * The "__profile__" sentinel is used by PlayerSettings to subscribe/unsubscribe
 * without needing a real team ID. When unsubscribing with "__profile__", the
 * hook removes the subscription entirely (since profile-only subs have no teams).
 *
 * @returns {object} {
 *   isSupported      — browser supports Web Push
 *   isIOS            — iOS device (push requires home screen install)
 *   isStandalone     — running as installed PWA (navigator.standalone)
 *   isSubscribed     — active push subscription exists in DB for this device
 *   subscribedTeamIds — array of team UUIDs currently followed
 *   isLoading        — async operation in progress
 *   error            — last error string or null
 *   subscribe(teamId, profileId?)  — follow a team or link profile; registers SW + requests permission
 *   unsubscribe(teamId)            — unfollow a team; removes SW sub if no teams/profile left
 * }
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";

/* ── Helpers ─────────────────────────────────────────────── */

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const isIOSDevice =
  /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

const isPushSupported =
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/* ── Hook ────────────────────────────────────────────────── */

export function usePushNotifications() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribedTeamIds, setSubscribedTeamIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cached reference to the PushSubscription object
  const pushSubRef = useRef(null);
  // Cached SW registration
  const swRegRef = useRef(null);

  const isStandalone =
    window.navigator.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;

  /* ── Register SW on mount (skip iOS until gesture) ─────── */
  useEffect(() => {
    if (!isPushSupported) return;
    // On iOS, SW registration must happen inside a user gesture.
    // We defer it to the subscribe() call instead.
    if (isIOSDevice) return;

    registerSW().then(async (reg) => {
      if (!reg) return;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        pushSubRef.current = existing;
        await syncSubscriptionState(existing);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Helpers ─────────────────────────────────────────────── */

  async function registerSW() {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      // Wait until the SW is active before using pushManager
      await navigator.serviceWorker.ready;
      swRegRef.current = reg;
      return reg;
    } catch (err) {
      console.error("[usePushNotifications] SW registration failed:", err);
      return null;
    }
  }

  /**
   * Query Supabase for this subscription's team list and sync local state.
   * Also sets isSubscribed=true if the row exists (even with empty team_ids),
   * which handles profile-only subscriptions.
   */
  async function syncSubscriptionState(pushSub) {
    const endpoint = pushSub.endpoint;
    const { data, error: dbErr } = await supabase
      .from("push_subscriptions")
      .select("team_ids, profile_id")
      .eq("endpoint", endpoint)
      .maybeSingle();

    if (dbErr) {
      console.error("[usePushNotifications] syncSubscriptionState:", dbErr);
      return;
    }

    if (data) {
      // Row exists — subscribed (may be profile-only with empty team_ids)
      setIsSubscribed(true);
      setSubscribedTeamIds(data.team_ids ?? []);
    } else {
      // SW subscription exists locally but not in DB — treat as unsubscribed
      setIsSubscribed(false);
      setSubscribedTeamIds([]);
    }
  }

  /**
   * Build the serialisable subscription payload Supabase expects.
   */
  function buildPayload(pushSub, teamIds) {
    const key = pushSub.getKey("p256dh");
    const authSecret = pushSub.getKey("auth");

    // Must be base64url (not standard base64) to match server-side decoding
    const toBase64url = (buffer) =>
      btoa(String.fromCharCode(...new Uint8Array(buffer)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");

    return {
      endpoint: pushSub.endpoint,
      p256dh: toBase64url(key),
      auth: toBase64url(authSecret),
      // Filter out the sentinel value — only real UUIDs go in team_ids
      team_ids: teamIds.filter((id) => id !== "__profile__"),
    };
  }

  /* ── subscribe(teamId, profileId?) ──────────────────────── */
  /**
   * Subscribe this device to push notifications.
   *
   * @param {string} teamId     - Team UUID to follow, OR "__profile__" for
   *                              profile-only subscription (no team targeting).
   * @param {string} [profileId] - User's profile UUID. When provided, the
   *                              subscription row is linked to this profile so
   *                              the sendToProfile Edge Function action can
   *                              target this device for per-user notifications.
   */
  const subscribe = useCallback(
    async (teamId, profileId = null) => {
      setError(null);

      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      console.log(
        "[usePushNotifications] VAPID key present:",
        !!vapidKey,
        "length:",
        vapidKey?.length,
      );

      // iOS: push only works when installed as PWA
      if (isIOSDevice && !window.navigator.standalone) {
        setError("ios-not-installed");
        return;
      }

      if (!isPushSupported) {
        setError("not-supported");
        return;
      }

      setIsLoading(true);
      try {
        // On iOS we register SW here (gesture context); elsewhere it's already done
        let reg = swRegRef.current;
        if (!reg) {
          reg = await registerSW();
          if (!reg) throw new Error("Service worker registration failed.");
        }

        // Request notification permission
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          setError("permission-denied");
          return;
        }

        // Get or create the PushSubscription
        let pushSub = await reg.pushManager.getSubscription();
        if (!pushSub) {
          const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
          if (!vapidKey) throw new Error("VAPID public key not configured.");
          pushSub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
        }
        pushSubRef.current = pushSub;

        // Merge teamId into existing team list (upsert pattern).
        // "__profile__" is a sentinel — it's filtered out of team_ids in buildPayload.
        const isProfileOnly = teamId === "__profile__";
        const existingIds = isProfileOnly
          ? subscribedTeamIds // don't add sentinel to team list
          : subscribedTeamIds.includes(teamId)
            ? subscribedTeamIds
            : [...subscribedTeamIds, teamId];

        const payload = buildPayload(pushSub, existingIds);

        // Include profile_id so the subscription can be targeted per-user
        if (profileId) {
          payload.profile_id = profileId;
        }

        // Save to Supabase via push-notifications (upsert on endpoint)
        const { data: fnData, error: fnErr } = await supabase.functions.invoke(
          "push-notifications",
          { body: { action: "saveSubscription", ...payload } },
        );

        if (fnErr) throw fnErr;
        if (fnData?.error) throw new Error(fnData.error);

        setIsSubscribed(true);
        setSubscribedTeamIds(existingIds);
      } catch (err) {
        console.error("[usePushNotifications] subscribe failed:", err);
        setError(err.message ?? "unknown-error");
      } finally {
        setIsLoading(false);
      }
    },
    [subscribedTeamIds],
  );

  /* ── unsubscribe(teamId) ─────────────────────────────────── */
  /**
   * Unsubscribe this device from push notifications for a team.
   *
   * @param {string} teamId - Team UUID to unfollow, OR "__profile__" to remove
   *                          the profile-only subscription entirely.
   *
   * When teamId is "__profile__" or no teams remain after removal, the SW
   * subscription is cancelled and the DB row is deleted entirely.
   */
  const unsubscribe = useCallback(
    async (teamId, profileId = null) => {
      setError(null);
      setIsLoading(true);
      try {
        const pushSub = pushSubRef.current;

        // "__profile__" sentinel or no teams remaining → remove entirely
        const isProfileOnly = teamId === "__profile__";
        const remainingIds = isProfileOnly
          ? []
          : subscribedTeamIds.filter((id) => id !== teamId);

        if (remainingIds.length === 0) {
          // No teams left (or profile-only unsubscribe) — remove SW subscription entirely

          if (pushSub) {
            await pushSub.unsubscribe();
            pushSubRef.current = null;
          }

          // Delete from Supabase.
          // Prefer endpoint-based delete (precise); fall back to profile-based
          // delete if the SW subscription was lost (e.g. browser data cleared).
          if (pushSub) {
            const { data: fnData, error: fnErr } =
              await supabase.functions.invoke("push-notifications", {
                body: {
                  action: "deleteSubscription",
                  endpoint: pushSub.endpoint,
                },
              });
            if (fnErr) throw fnErr;
            if (fnData?.error) throw new Error(fnData.error);
          } else if (profileId) {
            // Fallback: remove all subscriptions for this profile
            const { data: fnData, error: fnErr } =
              await supabase.functions.invoke("push-notifications", {
                body: {
                  action: "deleteByProfile",
                  profile_id: profileId,
                },
              });
            if (fnErr) throw fnErr;
            if (fnData?.error) throw new Error(fnData.error);
          }

          setIsSubscribed(false);
          setSubscribedTeamIds([]);
        } else {
          // Teams remain — just update the team list
          if (!pushSub) throw new Error("No active subscription found.");

          const payload = buildPayload(pushSub, remainingIds);
          const { data: fnData, error: fnErr } =
            await supabase.functions.invoke("push-notifications", {
              body: { action: "saveSubscription", ...payload },
            });
          if (fnErr) throw fnErr;
          if (fnData?.error) throw new Error(fnData.error);

          setSubscribedTeamIds(remainingIds);
        }
      } catch (err) {
        console.error("[usePushNotifications] unsubscribe failed:", err);
        setError(err.message ?? "unknown-error");
      } finally {
        setIsLoading(false);
      }
    },
    [subscribedTeamIds],
  );

  /* ── Return ──────────────────────────────────────────────── */
  return {
    isSupported: isPushSupported,
    isIOS: isIOSDevice,
    isStandalone,
    isSubscribed,
    subscribedTeamIds,
    isLoading,
    error,
    subscribe,
    unsubscribe,
  };
}
